// src/pages/customers/CustomerList.tsx
import { Column, DataTable } from '@/components/common/DataTable';
import DeleteConfirmDialog from '@/components/common/DeleteConfirmDialog';
import SelectDropdown from '@/components/common/SelectDropdown';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import useDebounce from '@/hooks/use-debounce';
import { DEFAULT_PAGINATION } from '@/lib/constants/common';
import { customerType, gstTypesOptions } from '@/lib/constants/customors';
import { customersAPI } from '@/lib/utils/api';
import { Pagiantion } from '@/types/common.type';
import { Customer, CustomerType } from '@/types/customer.types';
import { Edit, Files, Plus, Trash2 } from 'lucide-react';
import { ChangeEvent, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';

interface Option {
  label: string;
  value: string;
}

const CustomerList = () => {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [search, setSearch] = useState<string>('');
  const debouncedSearch = useDebounce(search, 300);
  const [filterType, setFilterType] = useState<CustomerType | 'all'>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(() => {
    const saved = localStorage.getItem('customers_rowsPerPage');
    return saved ? Number(saved) : 10;
  });
  const [totalCustomers, setTotalCustomers] = useState<number>(0);

  const handleRowsPerPageChange = (rows: number) => {
    setRowsPerPage(rows);
    localStorage.setItem('customers_rowsPerPage', String(rows));
  };

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [customerToDelete, setCustomerToDelete] = useState<Customer | null>(null);
  const [deleting, setDeleting] = useState(false);

  const fetchCustomers = async (): Promise<void> => {
    try {
      setLoading(true);

      const params: any = {};
      if (debouncedSearch) params.search = debouncedSearch;
      params.page = currentPage;
      params.limit = rowsPerPage;
      if (filterType && filterType !== 'all') params.customerType = filterType;

      const response = await customersAPI.getAll(params);
      const {
        total,
        limit = rowsPerPage,
        page,
        pages,
      }: Pagiantion = response.data?.pagination ?? DEFAULT_PAGINATION;
      setCustomers(
        Array.isArray(response.data.customers) ? response.data.customers : (response.data ?? [])
      );
      setTotalCustomers(total);
    } catch {
      toast.error('Failed to fetch customers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, rowsPerPage, filterType]);

  useEffect(() => {
    fetchCustomers();
  }, [currentPage, debouncedSearch, rowsPerPage, filterType]);

  const handleDeleteClick = (customer: Customer) => {
    setCustomerToDelete(customer);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!customerToDelete) return;

    try {
      setDeleting(true);
      await customersAPI.delete(customerToDelete._id);
      toast.success('Customer deleted successfully');
      fetchCustomers();
    } catch {
      toast.error('Failed to delete customer');
    } finally {
      setDeleting(false);
      setDeleteDialogOpen(false);
      setCustomerToDelete(null);
    }
  };

  const handleDuplicate = async (customer: Customer): Promise<void> => {
    try {
      const duplicatedData = { ...customer, _id: undefined };

      if (customer.customerType === 'business') {
        duplicatedData.companyName = `${customer.companyName ?? ''} `;
      } else {
        duplicatedData.primaryContact = `${customer.primaryContact} `;
      }

      await customersAPI.create(duplicatedData);
      toast.success('Customer duplicated successfully');
      fetchCustomers();
    } catch {
      toast.error('Failed to duplicate customer');
    }
  };

  const getLabel = (options: Option[], value?: string): string =>
    options.find((opt) => opt.value === value)?.label || value || '-';

  const columns: Column<Customer>[] = [
    {
      key: 'customer',
      header: 'Customer',
      cell: (customer) => (
        <>
          <div className="font-medium whitespace-nowrap w-fit min-w-fit">
            {customer.customerType === 'business' ? customer.companyName : customer.primaryContact}
          </div>
          {customer.customerType === 'business' && (
            <div className="text-sm text-muted-foreground whitespace-nowrap w-fit min-w-fit">
              {customer.primaryContact}
            </div>
          )}
        </>
      ),
    },
    {
      key: 'type',
      header: 'Type',
      cell: (customer) => (
        <Badge
          variant="secondary"
          className={`capitalize ${
            customer.customerType === 'business'
              ? 'bg-blue-100 text-blue-800'
              : 'bg-green-100 text-green-800'
          }`}
        >
          {customer.customerType}
        </Badge>
      ),
    },
    {
      key: 'contact',
      header: 'Contact',
      cell: (customer) => (
        <div className="text-sm">
          {customer.email && <div>{customer.email}</div>}
          {customer.phone && <div>{customer.phone}</div>}
        </div>
      ),
    },
    {
      key: 'gst',
      header: 'GST',
      cell: (customer) => (
        <div className="text-sm">
          <div className="text-muted-foreground whitespace-nowrap w-fit min-w-fit">
            {getLabel(gstTypesOptions, customer.gstTreatment?.gstType)}
          </div>
          {customer.gstTreatment?.gstNumber && (
            <div className="font-mono text-xs">{customer.gstTreatment.gstNumber}</div>
          )}
        </div>
      ),
    },
    {
      key: 'actions',
      header: 'Actions',
      className: 'text-right sticky right-0',
      cell: (customer) => (
        <div className="flex justify-end gap-2">
          <Button
            asChild
            variant="ghost"
            size="icon"
            className="text-blue-600 hover:bg-blue-50 hover:text-blue-600"
          >
            <Link to={`/customers/${customer._id}/edit`}>
              <Edit className="!h-[18px] !w-[18px]" />
            </Link>
          </Button>
          <Button variant="ghost" size="icon" onClick={() => handleDuplicate(customer)}>
            <Files className="!h-[18px] !w-[18px]" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="text-red-600 hover:bg-red-50 hover:text-red-600"
            onClick={() => handleDeleteClick(customer)}
          >
            <Trash2 className="!h-[18px] !w-[18px]" />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center flex-wrap gap-4">
        {/* <h1 className="text-3xl font-bold">Manage your customers</h1> */}
        <p className="text-muted-foreground mt-2">
          Manage all your customer information in one place
        </p>
        <Button asChild>
          <Link to="/customers/new">Add Customer</Link>
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-6">
          <div className="space-y-2">
            <Label>Search</Label>
            <Input
              type="search"
              placeholder="Search by customer name, company or email"
              value={search}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label>Customer Type</Label>
            <SelectDropdown
              value={filterType}
              placeholder="All"
              options={[{ label: 'All Customers Type', value: 'all' }, ...customerType]}
              onChange={(value) => setFilterType(value as CustomerType)}
            />
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardHeader>
          <CardTitle>Customer List</CardTitle>
          <CardDescription>
            {customers.length} customer{customers.length !== 1 ? 's' : ''} found
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-3">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="flex items-center space-x-4 rounded-lg border p-4">
                  <Skeleton className="h-12 w-12 rounded-full" />
                  <div className="space-y-2">
                    <Skeleton className="h-4 w-[250px]" />
                    <Skeleton className="h-4 w-[200px]" />
                  </div>
                </div>
              ))}
            </div>
          ) : customers.length === 0 ? (
            <div className="py-12 text-center">
              <h3 className="mb-2 text-lg font-semibold">No customers found</h3>
              <p className="mb-4 text-muted-foreground">
                {search
                  ? 'Try a different search term'
                  : 'Get started by adding your first customer.'}
              </p>
              <Button asChild>
                <Link to="/companies/new">
                  <Plus className="mr-2 h-4 w-4" />
                  Add Customer
                </Link>
              </Button>
            </div>
          ) : (
            <DataTable
              data={customers}
              columns={columns}
              currentPage={currentPage}
              rowsPerPage={rowsPerPage}
              total={totalCustomers}
              onPageChange={setCurrentPage}
              onRowsPerPageChange={handleRowsPerPageChange}
              emptyMessage="No customers found"
            />
          )}
        </CardContent>
      </Card>

      {/* Delete Dialog */}
      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        entityName={
          customerToDelete?.customerType === 'business'
            ? customerToDelete.companyName
            : customerToDelete?.primaryContact
        }
        loading={deleting}
        onDelete={handleDeleteConfirm}
      />
    </div>
  );
};

export default CustomerList;
